import React, { useEffect, useRef, useState } from "react";
import { X, Zap, Users, Check, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCurrency } from "../contexts/CurrencyContext";
import PayPalCheckout, { PayDivider, isNativeApp } from "./PayPalCheckout";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PaywallModal({ onClose, onSuccess }) {
  const { user, refreshUser } = useAuth();
  const { isUSD, locked, price, priceOld, priceInr, setCurrency } = useCurrency();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [step, setStep] = useState("plans"); // "plans" | "form"
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", phone: "" });
  const [error, setError] = useState("");
  const [waitingOrder, setWaitingOrder] = useState(null);
  const [waitingPaypal, setWaitingPaypal] = useState(null);

  // Capture the PayPal order once the customer approves it in the browser.
  useEffect(() => {
    if (!waitingPaypal) return;
    let alive = true;
    const check = async () => {
      try {
        const r = await axios.post(`${API}/payments/paypal/capture/${waitingPaypal}`, {}, { withCredentials: true });
        if (alive && (r.data.status === "success" || r.data.status === "COMPLETED")) {
          await refreshUser();
          onSuccess?.(selectedPlan?.id);
          onClose?.();
        }
      } catch (_) { /* not approved yet */ }
    };
    const timer = setInterval(check, 5000);
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    return () => { alive = false; clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); window.removeEventListener("focus", check); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitingPaypal]);

  // Android/browser back must move *within* the payment flow (payer form → plans →
  // close) instead of navigating away from the page and losing the checkout.
  // Hooks MUST live above any early return to satisfy react-hooks/rules-of-hooks.
  const stepRef = useRef(step);
  stepRef.current = step;
  const closedByPop = useRef(false);
  const pushed = useRef(false);
  useEffect(() => {
    if (!user) return;
    if (!pushed.current) {
      window.history.pushState({ afPaywall: true }, "");
      pushed.current = true;
    }
    const onPop = () => {
      if (stepRef.current === "form") {
        setStep("plans");
        window.history.pushState({ afPaywall: true }, "");
      } else {
        closedByPop.current = true;
        onClose?.();
      }
    };
    window.addEventListener("popstate", onPop);
    return () => {
      window.removeEventListener("popstate", onPop);
      // Drop our marker without navigating. Calling history.back() here would fire
      // popstate on React's double-invoked effects and close the modal instantly.
      if (!closedByPop.current && window.history.state?.afPaywall) {
        window.history.replaceState({}, "");
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Inside the Android app the WebView cannot list installed UPI apps, so we hand
  // the checkout to Chrome — it offers Google Pay / PhonePe / Paytm natively — and
  // poll our backend until the payment lands.
  const payInBrowser = async () => {
    setError("");
    setLoading(selectedPlan.id);
    try {
      const res = await axios.post(`${API}/payments/create-order`, {
        plan: selectedPlan.id, name: form.name, email: form.email, phone: form.phone
      }, { withCredentials: true });
      const { order_id, payment_session_id, environment } = res.data;
      const url = `${window.location.origin}/pay?session=${encodeURIComponent(payment_session_id)}`
        + `&order=${encodeURIComponent(order_id)}&plan=${selectedPlan.id}&env=${environment || "production"}`;
      if (window.AstroFateNative?.openExternal) window.AstroFateNative.openExternal(url);
      else window.open(url, "_blank", "noopener");
      setWaitingOrder(order_id);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not start the payment. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  useEffect(() => {
    if (!waitingOrder) return;
    let alive = true;
    const check = async () => {
      try {
        const r = await axios.post(`${API}/payments/verify/${waitingOrder}`, {}, { withCredentials: true });
        if (alive && r.data.status === "success") {
          await refreshUser();
          onSuccess?.(selectedPlan?.id);
          onClose?.();
        }
      } catch (_) { /* still pending */ }
    };
    const timer = setInterval(check, 5000);
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", check);
    return () => { alive = false; clearInterval(timer); document.removeEventListener("visibilitychange", onVisible); window.removeEventListener("focus", check); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [waitingOrder]);

  const plans = [
    {
      id: "ai_chat", icon: <Zap size={24} />, title: "AI Chat", price: price("ai_chat"),
      period: "24 hours",
      color: "#FFD166",
      features: ["4 AI astrology questions", "Personalized chart memory", "Career & love guidance", "Remedies & predictions", "Mahadasha insights", "Valid for 24 hours"]
    },
    {
      id: "consultation", icon: <Users size={24} />, title: "Talk to Astrologer",
      price: price("consultation"), oldPrice: priceOld("consultation"),
      period: "15 min live chat",
      badge: "50% OFF — Limited",
      color: "#9D4EDD",
      features: ["Live astrologer chat (15 min)", "Same-day instant slot available", "Priority kundli report", "Personalized remedies", "Marriage & career analysis"]
    }
  ];

  if (!user) {
    return (
      <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.8)", backdropFilter: "blur(8px)", padding: "16px" }}>
        <div className="glass-card" style={{ padding: "40px", maxWidth: "380px", width: "100%", textAlign: "center", background: "rgba(18,0,33,0.95)" }}>
          <Lock size={48} style={{ color: "#FFD166", marginBottom: "16px" }} />
          <h2 className="font-cinzel" style={{ fontSize: "1.5rem", color: "#FFD166", marginBottom: "12px" }}>Login Required</h2>
          <p style={{ color: "rgba(255,255,255,0.6)", marginBottom: "24px" }}>Please login to access premium features</p>
          <button onClick={() => { onClose?.(); navigate("/auth"); }} className="btn-gold" style={{ width: "100%" }}>Login / Sign Up</button>
          <button onClick={onClose} style={{ marginTop: "12px", background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.4)", width: "100%" }}>Cancel</button>
        </div>
      </div>
    );
  }

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setStep("form");
  };

  // In the Android app the in-WebView checkout cannot list installed UPI apps, so
  // INR payments are handed to Chrome where Google Pay / PhonePe / Paytm all show.
  const nativeInr = isNativeApp() && !isUSD;
  // PayPal's JS buttons cannot run inside a WebView either, so USD payments in the
  // app open the PayPal approval page in Chrome and we capture it on return.
  const nativeUsd = isNativeApp() && isUSD;
  const formReady = !!(form.name.trim() && form.email.trim() && form.phone.trim());

  const payPaypalInBrowser = async () => {
    setError("");
    setLoading(selectedPlan.id);
    try {
      const res = await axios.post(`${API}/payments/paypal/create-order`, {
        plan: selectedPlan.id, name: form.name, email: form.email, phone: form.phone
      }, { withCredentials: true });
      const { approve_url, paypal_order_id } = res.data;
      if (!approve_url) throw new Error("no approve url");
      if (window.AstroFateNative?.openExternal) window.AstroFateNative.openExternal(approve_url);
      else window.open(approve_url, "_blank", "noopener");
      setWaitingPaypal(paypal_order_id);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not start the PayPal checkout. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    if (nativeUsd) return payPaypalInBrowser();
    if (nativeInr) return payInBrowser();
    return handleInAppCheckout();
  };

  const handleInAppCheckout = async () => {
    setError("");
    setLoading(selectedPlan.id);
    try {
      const res = await axios.post(`${API}/payments/create-order`, {
        plan: selectedPlan.id, name: form.name, email: form.email, phone: form.phone
      }, { withCredentials: true });
      const { order_id, payment_session_id, mock, environment } = res.data;

      if (mock) {
        // Local dev only - no live keys configured
        await axios.post(`${API}/payments/verify/${order_id}`, {}, { withCredentials: true });
        await refreshUser();
        onSuccess?.(selectedPlan.id);
        onClose?.();
        return;
      }

      // Real Cashfree checkout via v3 SDK
      if (!window.Cashfree) {
        setError("Payment SDK not loaded. Please refresh the page and try again.");
        return;
      }
      const mode = environment === "production" ? "production" : "sandbox";
      const cashfree = window.Cashfree({ mode });
      const result = await cashfree.checkout({
        paymentSessionId: payment_session_id,
        redirectTarget: "_modal",
      });
      // After modal closes, verify with our backend (which talks to Cashfree to confirm)
      if (result?.error) {
        setError(result.error.message || "Payment was cancelled or failed.");
        return;
      }
      try {
        const verifyRes = await axios.post(`${API}/payments/verify/${order_id}`, {}, { withCredentials: true });
        if (verifyRes.data.status === "success") {
          await refreshUser();
          onSuccess?.(selectedPlan.id);
          onClose?.();
        } else {
          setError("Payment could not be verified. If you completed the payment, please contact support.");
        }
      } catch (vErr) {
        const detail = vErr.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "Verification failed. If you completed the payment, please contact support.");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Payment failed. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div data-testid="paywall-modal" style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.85)", backdropFilter: "blur(12px)", padding: "16px" }}>
      <div className="glass-card animate-fadeInUp paywall-shell" style={{ maxWidth: step === "form" ? "420px" : "700px", width: "100%", background: "rgba(18,0,33,0.97)", border: "1px solid rgba(255,209,102,0.15)", padding: "32px", position: "relative", maxHeight: "90vh", overflowY: "auto" }}>
        <button data-testid="paywall-close" onClick={onClose} style={{ position: "absolute", top: "20px", right: "20px", background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.4)" }}>
          <X size={20} />
        </button>

        {step === "plans" ? (
          <>
            <div style={{ textAlign: "center", marginBottom: "28px" }}>
              <h2 className="font-cinzel gradient-text-gold" style={{ fontSize: "1.6rem", marginBottom: "8px" }}>Continue Your Reading</h2>
              <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.9rem" }}>Choose a plan to unlock personalized cosmic insights</p>
            </div>
            <div className="paywall-plans" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              {plans.map(plan => (
                <div key={plan.id} data-testid={`plan-${plan.id}`} className="glass-card" style={{ padding: "24px", border: `1px solid ${plan.color}30`, cursor: "pointer", transition: "all 0.3s ease" }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = plan.color + "80"}
                  onMouseLeave={e => e.currentTarget.style.borderColor = plan.color + "30"}
                  onClick={() => handleSelectPlan(plan)}>
                  <div style={{ color: plan.color, marginBottom: "12px" }}>{plan.icon}</div>
                  {plan.badge && (
                    <div style={{ display: "inline-block", background: "linear-gradient(135deg, #ef4444, #f97316)", color: "white", padding: "3px 10px", borderRadius: "50px", fontSize: "0.66rem", fontWeight: 700, letterSpacing: "0.3px", marginBottom: "8px", textTransform: "uppercase" }}>{plan.badge}</div>
                  )}
                  <div style={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
                    <div className="font-cinzel" style={{ fontSize: "1.6rem", color: plan.color, fontWeight: "700" }}>{plan.price}</div>
                    {plan.oldPrice && (
                      <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.95rem", textDecoration: "line-through" }}>{plan.oldPrice}</div>
                    )}
                  </div>
                  <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.75rem", marginBottom: "12px" }}>{plan.period}</div>
                  <div style={{ fontWeight: "700", color: "white", marginBottom: "16px" }}>{plan.title}</div>
                  <ul style={{ listStyle: "none", padding: 0 }}>
                    {plan.features.map((f, i) => (
                      <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "8px", color: "rgba(255,255,255,0.6)", fontSize: "0.8rem", marginBottom: "8px" }}>
                        <Check size={12} style={{ color: plan.color, flexShrink: 0, marginTop: "3px" }} /> {f}
                      </li>
                    ))}
                  </ul>
                  <button data-testid={`select-plan-${plan.id}`} className={plan.id === "ai_chat" ? "btn-gold" : "btn-purple"} style={{ width: "100%", marginTop: "16px", padding: "10px" }}>
                    Get {plan.title}
                  </button>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <button onClick={() => setStep("plans")} style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.5)", marginBottom: "20px", display: "flex", alignItems: "center", gap: "6px", fontSize: "0.85rem" }}>
              ← Back to plans
            </button>
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              <div style={{ fontSize: "2rem", color: selectedPlan.color, fontWeight: "700", fontFamily: "'Cinzel', serif" }}>{selectedPlan.price}</div>
              <div style={{ color: "white", fontWeight: "600", marginTop: "4px" }}>{selectedPlan.title}</div>
            </div>
            <form onSubmit={handlePayment}>
              {[{ key: "name", label: "Full Name", type: "text" }, { key: "email", label: "Email", type: "email" }, { key: "phone", label: "Phone Number", type: "tel" }].map(field => (
                <div key={field.key} style={{ marginBottom: "16px" }}>
                  <label style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.8rem", display: "block", marginBottom: "6px" }}>{field.label}</label>
                  <input data-testid={`payment-${field.key}-input`} type={field.type} value={form[field.key]} onChange={e => setForm({...form, [field.key]: e.target.value})} className="cosmic-input" required />
                </div>
              ))}
              {error && <div style={{ color: "#fca5a5", fontSize: "0.85rem", background: "rgba(239,68,68,0.1)", padding: "10px", borderRadius: "8px", marginBottom: "16px" }}>{error}</div>}
              {(waitingOrder || waitingPaypal) && (
                <div data-testid="pay-waiting-note" style={{ color: "#FFD166", fontSize: "0.82rem", background: "rgba(255,209,102,0.08)", padding: "10px 12px", borderRadius: "8px", marginBottom: "16px", lineHeight: 1.55 }}>
                  Complete the payment in your browser / UPI app, then come back here — your plan activates automatically.
                </div>
              )}
              <div style={{ display: "flex", flexDirection: (isUSD && !nativeUsd) ? "column-reverse" : "column" }}>
                {!(isUSD && locked && !nativeUsd) && (
                <button data-testid="payment-submit-btn" type="submit" disabled={!!loading} className={selectedPlan.id === "ai_chat" ? "btn-gold" : "btn-purple"} style={{ width: "100%", opacity: loading ? 0.7 : 1 }}>
                  {loading ? "Processing..."
                    : nativeUsd ? `Pay ${selectedPlan.price} with PayPal`
                    : nativeInr ? `Pay ${priceInr(selectedPlan.id)} — UPI / Card`
                    : `Pay ${priceInr(selectedPlan.id)}`}
                </button>
                )}
                {isUSD && !nativeUsd && (
                  <>
                    {(!locked || formReady) ? (
                      <PayPalCheckout
                        plan={selectedPlan.id}
                        payer={form}
                        variant="primary"
                        label="Pay"
                        onPaid={async () => {
                          await refreshUser();
                          onSuccess?.(selectedPlan.id);
                          onClose?.();
                        }}
                        onError={(m) => setError(m)}
                      />
                    ) : (
                      <p data-testid="pay-fill-details-note" style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.8rem", textAlign: "center", margin: "6px 0 0" }}>
                        Fill in your details above to continue to PayPal.
                      </p>
                    )}
                    {!locked && <PayDivider text="or pay in ₹ (UPI / card)" />}
                  </>
                )}
              </div>
              {nativeUsd && (
                <>
                  <p data-testid="pay-paypal-note" style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.74rem", marginTop: "10px", textAlign: "center", lineHeight: 1.5 }}>
                    Opens PayPal securely in your browser — pay with your PayPal balance or any card.
                  </p>
                  {!locked && (
                    <button type="button" data-testid="pay-inr-switch-btn" onClick={() => setCurrency("INR")}
                      style={{ width: "100%", marginTop: "8px", background: "none", border: "1px dashed rgba(255,209,102,0.4)", borderRadius: "10px", padding: "9px", color: "rgba(255,209,102,0.85)", fontSize: "0.78rem", cursor: "pointer" }}>
                      Pay in ₹ instead (UPI / card)
                    </button>
                  )}
                </>
              )}
              {nativeInr && (
                <>
                  <p data-testid="pay-upi-note" style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.74rem", marginTop: "10px", textAlign: "center", lineHeight: 1.5 }}>
                    Opens the secure checkout where Google Pay, PhonePe, Paytm, cards and netbanking are all available.
                  </p>
                  <button type="button" data-testid="pay-inapp-btn" onClick={handleInAppCheckout} disabled={!!loading}
                    style={{ width: "100%", marginTop: "8px", background: "none", border: "1px dashed rgba(255,209,102,0.4)", borderRadius: "10px", padding: "9px", color: "rgba(255,209,102,0.85)", fontSize: "0.78rem", cursor: "pointer" }}>
                    Prefer to stay in the app? Pay by card here
                  </button>
                </>
              )}
            </form>
          </>
        )}
      </div>
    </div>
  );
}
